// Aura-driven artifact restrictions + per-artifact bearer damage.
// Cursed Iron pattern: "Artifacts here can't be dropped and each
// deals 1 damage to its bearer at the end of each turn."
//
// Two surfaces:
//
//   `lockedEquipmentIds(meshes, cellAt, getFullCard)`. Set<id> of
//   equipped-artifact card ids whose host sits on a cell covered by
//   any aura carrying a BAN_ARTIFACT_DETACH_AT_COVERED static entry.
//   The UI (CardInspector) reads this set to gate the Detach button.
//   W4: server-consumed via sync. The server derives
//   `boardLegality.lockedEquipmentIds` from it and rejects a
//   SET_EQUIPMENT dropping a locked artifact; auth clients read the
//   bag from the mirror instead of calling this locally (sandbox
//   still does).
//
//   `artifactsAndBearersInCoveredCells(sourceCard, meshes, cellAt)`
// { artifact, bearer } pairs for the DAMAGE_BEARER_PER_ARTIFACT
//   op so each artifact's bearer takes its share of damage.
//
// Pure. No GameBoard state.

import { anchorCoveredCells } from '../../core/auraPlacement';
import { OP, TRIGGER } from '../schema';
import { msg } from '../../../logMessage';

/**
 * Union of every covered cell across in-play auras whose automation
 * carries a static `BAN_ARTIFACT_DETACH_AT_COVERED` entry.
 */
function bannedDetachCells(meshes, getFullCard) {
  const out = new Set();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || card.type !== 'Aura') continue;
    if (!card._auraShape || !card._auraAnchor) continue;
    const full = getFullCard(card.cardId);
    if (!full) continue;
    const entries = (full.automation || []).filter((e) => e.trigger === TRIGGER.STATIC);
    let restricts = false;
    for (const e of entries) {
      for (const eff of (e.effect || [])) {
        if (eff.op === OP.BAN_ARTIFACT_DETACH_AT_COVERED) { restricts = true; break; }
      }
      if (restricts) break;
    }
    if (!restricts) continue;
    for (const key of anchorCoveredCells(card._auraAnchor, card._auraShape.shape)) {
      out.add(key);
    }
  }
  return out;
}

/**
 * Set<id> of equipped-artifact card ids whose host is on a banned
 * cell. UI consults this to disable the detach button.
 */
export function lockedEquipmentIds(meshes, cellAt, getFullCard) {
  const banned = bannedDetachCells(meshes, getFullCard);
  const out = new Set();
  if (banned.size === 0) return out;
  for (const [, mesh] of meshes) {
    const host = mesh?.userData?.cardInstance;
    if (!host || !host.equippedCards?.length) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell || !banned.has(`${cell.col},${cell.row}`)) continue;
    for (const eq of host.equippedCards) out.add(eq.id);
  }
  return out;
}

/**
 * Pairs of `{ artifact, bearer }` for the bearer-damage op. Walks
 * each card on a covered cell and emits one pair per equipped
 * artifact attached to it.
 */
export function artifactsAndBearersInCoveredCells(sourceCard, meshes, cellAt) {
  if (!sourceCard?._auraShape || !sourceCard?._auraAnchor) return [];
  const covered = new Set(anchorCoveredCells(sourceCard._auraAnchor, sourceCard._auraShape.shape));
  if (covered.size === 0) return [];
  const out = [];
  for (const [, mesh] of meshes) {
    const host = mesh?.userData?.cardInstance;
    if (!host || !host.equippedCards?.length) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell || !covered.has(`${cell.col},${cell.row}`)) continue;
    for (const eq of host.equippedCards) out.push({ artifact: eq, bearer: host });
  }
  return out;
}

/**
 * Silent-op resolver for DAMAGE_BEARER_PER_ARTIFACT_IN_SCOPE.
 */
export function applyDamageBearerPerArtifactInScope(sourceCard, args, host) {
  const items = artifactsAndBearersInCoveredCells(sourceCard, host.getMeshes(), host.getCellAt());
  const amount = args?.amount ?? 1;
  for (const { artifact, bearer } of items) {
    host.logAction('{source} deals {n} damage to {target}', { source: artifact.name || msg('Artifact'), n: amount, target: bearer.name || msg('bearer') });
    host.damageTarget(bearer, amount, {
      sourceLabel: artifact.name || sourceCard.name || 'Source',
      log: false,
      sweep: true,
      floatingDamage: true,
    });
  }
}

/**
 * For every host in the source's covered set, send each equipped
 * artifact to its owner's cemetery. Used by Year of the Blaze /
 * The Great Drowning of Men to ensure "destroy everything here"
 * literally includes artifacts attached to hosts. Without this, the
 * host's `_detachAllEquipmentToField` (fired inside `sendCardToPile`
 * during the subsequent `kill_each`) would drop the artifacts back
 * onto the field and they'd survive untouched.
 *
 * Host adapter contract:
 *   sendToCemetery(card): void
 *   detachEquippedFromHost(host, artifactId): void. See GameBoard
 */
export function applyDestroyEquippedInScope(sourceCard, host) {
  const items = artifactsAndBearersInCoveredCells(sourceCard, host.getMeshes(), host.getCellAt());
  for (const { artifact, bearer } of items) {
    // Pop the equipped entry off the bearer FIRST so the host's
    // standard send-to-cemetery flow doesn't trigger an auto-detach
    // that puts the artifact back on the field.
    host.detachEquippedFromHost(bearer, artifact.id);
    host.logAction('{source} destroys {target}', { source: sourceCard.name || msg('Aura'), target: artifact.name || msg('an artifact') });
    host.sendToCemetery(artifact);
  }
}
